/**
 * 业务请求的桥适配器。
 *
 * 把 `window.dmexBridge.request` 包装成 **axios 兼容的响应形状**，这样调用点几乎不用改：
 *
 *   原来: const rsp = await instance.get(url, { params })   // axios 响应 { status, data }
 *   现在: const rsp = await bridgeRequest({ path, method, params })
 *
 * 为什么这么做（而不是让每个调用点直接调桥）：
 *  · tweb 有 20 个端点分散在多个模块，逐个改既要动很多处，还会让返回形状在各处不一致；
 *  · 真正需要变的只有「谁来发这个请求」这一件事。
 *
 * 三条关键规则：
 *
 * 1. **只支持相对路径**（发给 dmex 的 API 基地址）。
 *    非 dmex 后端的接口（AI 服务、n8n、Telegram Bot）暂不走这里：走桥就得让客户端
 *    把「dmex 的登录取证」或「设备指纹头」带到第三方域名上，那是凭据/身份泄漏。
 *    这些接口的去向待确认后再单独处理。
 *
 * 2. **失败一律 reject**，与 axios 在传输层失败时的行为一致
 *    （调用点普遍写了 `.catch()` 兜底，改动它们的失败语义会连带影响那些兜底逻辑）。
 *    注意：**HTTP 200 + 业务码非 200 不算失败** —— 老接口的成败写在 body 的业务码里，
 *    由调用方自己判断，这里不替它决定。所以返回的 `data` 是**完整信封**，见 toEnvelope。
 *
 * 3. **未绑定工作组时不发请求**（见 `hasWorkMemberContext`）。
 */

/** 与主进程 `dmex:page-api-result` 的回执形状对齐 */
interface BridgeResult {
  ok: boolean
  status?: number
  code?: number
  message?: string
  success?: boolean
  data?: unknown
  reason?: string
}

/**
 * 旧式路径的基地址前缀，从 `VITE_BASE_URL` 里取。
 *
 * 为什么需要：tweb 的请求路径是**相对 `VITE_BASE_URL`** 的，而那个变量本身含 `/v1`
 * （如 `https://api.dmex.io/v1`）。改造前由 axios 的 baseURL 负责拼接，所以调用点写
 * `url: "/fans/get-meta"` 最终会打到 `/v1/fans/get-meta`。
 * 现在请求交给客户端代发，这个前缀必须由这里补上 —— 否则既会被主进程的白名单拒掉
 * （白名单按真实路径 `/v1/fans/...` 登记），拼出来的地址也会少一段 `/v1`。
 *
 * 从环境变量推导而不是硬编码 `/v1`：不同 profile 的基地址可能不同，硬编码会在换环境时
 * 静默失效。
 */
const BASE_PATH = (() => {
  try {
    const base = String(import.meta.env.VITE_BASE_URL || '')
    if (!base) return ''
    const path = new URL(base, 'https://placeholder.invalid').pathname
    return path.replace(/\/+$/, '')
  } catch {
    return ''
  }
})()

/**
 * 把调用点给的路径解析成**从站点根开始的完整路径**。
 *
 * 两种写法并存，因为接口分两代：
 *  · 旧接口写作 `"/fans/get-meta"`，相对 `VITE_BASE_URL`（含 `/v1`）→ 需要补前缀；
 *  · 新接口写作 `"/api/v1/client/workflows/invoke"`，已经是完整路径 → 不能再补，
 *    否则会拼成 `/v1/api/v1/...`。
 *
 * 判别依据是新接口一律以 `/api/` 开头（旧接口是 `/v1/` 之后的段，如 `/fans`）。
 */
function resolvePath(path: string): string {
  if (!path.startsWith('/')) return path
  if (path.startsWith('/api/')) return path
  if (BASE_PATH && path.startsWith(`${BASE_PATH}/`)) return path
  return `${BASE_PATH}${path}`
}

/** axios 兼容的最小响应形状 */
export interface BridgeResponse<T = any> {
  status: number
  data: T
}

export interface BridgeRequestOptions {
  /** 相对 dmex API 基地址的路径，如 `/v1/fans/get-meta` */
  path: string
  method?: string
  params?: Record<string, unknown>
  data?: unknown
}

interface DmexBridge {
  request: (request: BridgeRequestOptions) => Promise<BridgeResult>
}

/**
 * 取桥。没有桥说明这个页面不是被 dmex 内嵌运行的
 * （例如直接在浏览器里打开 tweb）。
 *
 * **不准备回退到直连**：鉴权已统一到 dmex 客户端，页面自己手里没有任何可用凭据，
 * 直连只会得到 401。所以这里明确失败并说清原因，而不是发一个注定失败的请求。
 */
function getBridge(): DmexBridge | null {
  const bridge = (globalThis as { dmexBridge?: DmexBridge }).dmexBridge
  if (!bridge || typeof bridge.request !== 'function') return null
  return bridge
}

/** 把桥的失败原因转成调用方能看懂的错误 */
function toError(result: BridgeResult): Error {
  switch (result.reason) {
    case 'no-token':
      return new Error('工作台登录态不可用，请重新登录后再试')
    case 'timeout':
      return new Error('请求超时，请检查网络后重试')
    case 'network':
      return new Error('网络异常，请检查网络后重试')
    case 'path-not-allowed':
      return new Error('该接口未在客户端登记，请联系管理员')
    default:
      return new Error(result.message || '请求失败，请稍后重试')
  }
}

/**
 * 当前是否已拿到工作组绑定信息。
 *
 * 判据是 `tolink_groupMemberId`（工作号成员 id）—— 由客户端在确认绑定后下发
 * （见 utils/pcApi.js 的 `window.setTolinkBinding`）。
 *
 * 为什么用它作为判据，而不是「客户端说没绑」这类显式状态：本模块能发出去的**每一个**
 * 端点都是**工作号成员维度**的数据（fans / group-member / strategy / online/heart /
 * workflows/invoke）。没有成员上下文，这些请求在服务端**本来就不可能成功** ——
 * 所以这不是一个策略猜测，而是"缺必要输入"。
 */
function hasWorkMemberContext(): boolean {
  try {
    return !!sessionStorage.getItem('tolink_groupMemberId')
  } catch {
    // sessionStorage 不可用（隐私模式等）时不要把整个页面拦死
    return true
  }
}

/**
 * 未绑定时的日志节流。
 *
 * 必须节流：拦截点在每条消息都会触发的路径上（`getAIReplyStatus` → `get-config`），
 * 不节流就会把「拦截日志」刷成新的刷屏 —— 正是这次要解决的问题本身。
 */
const BLOCK_LOG_TTL_MS = 30 * 1000
const blockLoggedAt = new Map<string, number>()

function logBlockedOnce(path: string): void {
  const at = blockLoggedAt.get(path)
  if (at && Date.now() - at < BLOCK_LOG_TTL_MS) return
  blockLoggedAt.set(path, Date.now())
  console.warn('[bridge] 未绑定工作组，已拦截业务请求', {
    path,
    hint: '等待客户端下发工作组绑定信息（window.setTolinkBinding）后自动恢复'
  })
}

/**
 * 把主进程的回执还原成 axios 的响应形状 `{ status, data }`。
 *
 * **`data` 必须是完整的信封**（`{ code, message, success, data }`），不能只给业务载荷。
 * 这是 axios 的语义：`response.data` 是响应体，而这个后端的响应体就是信封。
 *
 * 这一点曾经搞错过，后果是一整片功能静默失效 —— 所有老调用点都在读信封上的字段：
 *   · `fansApi` 的 `rsp.code == 200` 才写「已上报」缓存 → 丢了 code 就永不写缓存，
 *     于是每次刷新页面都把已报过的粉丝重报一遍（去重彻底失效）；
 *   · `appDialogsManager` 的 `res.data.comment` → 丢了信封就取不到备注，还抛异常；
 *   · `getConfig` / `AiReply` 的 `res.code === 200`、`res.data.aiReply` 同样全落空。
 *
 * 主进程给的是**摊平**的形状（`{ok, status, code, message, success, data}`），
 * 所以这里要重新组装回信封。改了回执形状就要同步改这里。
 */
function toEnvelope(result: BridgeResult): Record<string, unknown> {
  return {
    code: result.code,
    message: result.message,
    success: result.success,
    data: result.data
  }
}

export async function bridgeRequest<T = any>(
  options: BridgeRequestOptions
): Promise<BridgeResponse<T>> {
  const bridge = getBridge()
  if (!bridge) {
    throw new Error('当前不在 Dmex 客户端内运行，无法调用业务接口')
  }

  // 未绑定工作组：直接失败，不要发出去。
  //
  // 不发的理由不是"省一次请求"，而是**发了会造成伤害**：这些请求必然被网关拒
  // （401 access denied），而调用点在每条消息里都会重试 —— 线上真实刷出过几百个 401。
  // 拦在这里，绑定信息一到就自动恢复，不需要重载页面。
  if (!hasWorkMemberContext()) {
    logBlockedOnce(options.path)
    throw new Error('尚未加入工作组，暂时无法使用该功能')
  }

  const result = await bridge.request({
    path: resolvePath(options.path),
    method: (options.method || 'get').toLowerCase(),
    params: options.params,
    data: options.data
  })

  if (!result?.ok) throw toError(result || { ok: false })

  // status 缺失时按 200 处理：能走到这里说明请求已经成功发出并拿到了响应，
  // 而老接口一律回 200、成败看业务码
  return {
    status: typeof result.status === 'number' ? result.status : 200,
    data: toEnvelope(result) as T
  }
}

/** GET 便捷封装，对应原来的 `instance.get(url, { params })` */
export function bridgeGet<T = any>(
  path: string,
  params?: Record<string, unknown>
): Promise<BridgeResponse<T>> {
  return bridgeRequest<T>({ path, method: 'get', params })
}

/** POST 便捷封装，对应原来的 `instance.post(url, data)` */
export function bridgePost<T = any>(
  path: string,
  data?: unknown,
  params?: Record<string, unknown>
): Promise<BridgeResponse<T>> {
  return bridgeRequest<T>({ path, method: 'post', data, params })
}

/** 供调用方判断当前是否运行在 dmex 内嵌环境里 */
export function hasDmexBridge(): boolean {
  return getBridge() !== null
}
